type RouteHandler = (params: Record<string, string>) => Promise<void> | void;

type RouteDef = {
  pattern: RegExp;
  paramNames: string[];
  handler: RouteHandler;
};

const routes: RouteDef[] = [];

/**
 * Clear all registered routes. Use at the top of a (re-)bootstrap to avoid
 * route-list duplication when bootstrap() runs multiple times in one session
 * (e.g. login → success → re-bootstrap appends to the same module-level array).
 */
export function resetRoutes(): void {
  routes.length = 0;
}

/**
 * Register a route. Path syntax: `/transactions/:id`. The `:id` segment is
 * captured as `params.id`. First-registered wins on overlapping patterns.
 */
export function route(path: string, handler: RouteHandler): void {
  const paramNames: string[] = [];
  const pattern = new RegExp(
    '^' + path.replace(/:([a-zA-Z_]+)/g, (_, name) => {
      paramNames.push(name);
      return '([^/]+)';
    }) + '/?$'
  );
  routes.push({ pattern, paramNames, handler });
}

/** Dispatch the current `window.location.pathname` to the matching route. */
export async function dispatch(): Promise<void> {
  const path = window.location.pathname;
  for (const r of routes) {
    const m = path.match(r.pattern);
    if (m) {
      const params: Record<string, string> = {};
      r.paramNames.forEach((name, i) => { params[name] = m[i + 1]; });
      await r.handler(params);
      return;
    }
  }
}

/** Programmatic navigation: pushes a history entry then dispatches. */
export async function navigate(path: string): Promise<void> {
  if (window.location.pathname === path) {
    await dispatch();
    return;
  }
  window.history.pushState({}, '', path);
  await dispatch();
}

/** Wire dispatch to popstate (back/forward buttons). Call once at startup. */
export function startRouter(): void {
  window.addEventListener('popstate', () => { void dispatch(); });
}
